import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AppointmentScheduling() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  const faqs = [
    {
      question: "Does your appointment scheduling system integrate with my existing calendar?",
      answer: "Yes! Our AI appointment scheduling system seamlessly integrates with all major calendar platforms including Google Calendar, Outlook, Apple Calendar, and Office 365. The two-way sync ensures that appointments booked through our system automatically appear in your calendar, and your existing calendar blocks prevent double-bookings. You can connect multiple calendars to check availability across your entire team."
    },
    {
      question: "How do automated reminders help reduce no-shows?",
      answer: "Our system sends customizable automated reminders via SMS, email, or both at intervals you choose (typically 24 hours and 1 hour before appointments). Studies show that automated reminders reduce no-shows by up to 38%. Clients can also confirm, reschedule, or cancel directly from the reminder message, giving you advance notice to fill the slot. You can customize reminder content, timing, and frequency for different appointment types."
    },
    {
      question: "Can clients book appointments outside of business hours?",
      answer: "Absolutely! One of the biggest advantages of our AI appointment scheduling system is 24/7 availability. Clients can book appointments at their convenience—whether it's 2 AM on a Sunday or during their lunch break. The system checks your real-time availability and only offers open slots, so you never have to worry about double-bookings or manual confirmation. This convenience significantly increases booking rates."
    },
    {
      question: "How does the system handle different types of appointments and services?",
      answer: "Our scheduling system supports unlimited appointment types, each with custom durations, buffer times, pricing, and booking rules. For example, you can set up 30-minute consultations, 90-minute deep-dive sessions, and 15-minute follow-ups—all with different availability windows. You can also create service packages, require deposits for certain appointment types, and set minimum advance notice periods. The booking page automatically displays only relevant options based on the client's needs."
    },
    {
      question: "What happens if a client needs to reschedule or cancel?",
      answer: "Clients can easily reschedule or cancel appointments through the link in their confirmation email or reminder messages, up to a deadline you set (e.g., 24 hours before). The system automatically updates your calendar and notifies you of the change. You can configure cancellation policies, require reasons for cancellations, and even automatically offer the freed slot to waitlisted clients. This self-service approach saves you countless hours of back-and-forth communication."
    },
    {
      question: "Can I manage appointments for multiple team members or locations?",
      answer: "Yes! Our team scheduling features allow you to manage appointments for multiple staff members, each with their own calendar, availability rules, and service offerings. Clients can choose a specific team member or let the system assign based on availability. You can also manage multiple locations, each with different hours and services. The admin dashboard gives you a bird's-eye view of all appointments across your entire organization."
    },
    {
      question: "How customizable is the booking page?",
      answer: "Extremely customizable! You can fully brand your booking page with your logo, colors, and custom domain. Add intake forms to collect information before appointments, display your cancellation policy, show staff bios and photos, and include custom fields for specific appointment types. The booking page is mobile-responsive and can be embedded directly on your website or shared as a standalone link. You control every aspect of the client booking experience."
    },
    {
      question: "What kind of reporting and analytics do you provide?",
      answer: "Our analytics dashboard provides comprehensive insights including total bookings, no-show rates, most popular services and time slots, revenue tracking, client retention metrics, and booking source attribution. You can see which marketing channels drive the most appointments, identify peak demand periods to optimize staffing, and track individual team member performance. All reports can be exported and customized by date range, service type, or team member."
    }
  ];

  return (
    <>
      <SEO
        title="AI Appointment Scheduling | Automated Booking System"
        description="Streamline your booking process with AI appointment scheduling. Automated calendar management, smart scheduling, and 24/7 booking availability for your business."
        canonical="/ai-appointment-scheduling"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Appointment Scheduling',
              description: 'Automated AI-powered appointment scheduling and booking system for businesses',
              provider: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: import.meta.env.VITE_SITE_URL || 'https://example.com'
              },
              areaServed: 'Worldwide',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/ai-appointment-scheduling`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'How does AI appointment scheduling work?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'AI appointment scheduling automatically manages your calendar, handles booking requests, sends reminders, and optimizes scheduling based on availability and preferences.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Can customers book appointments 24/7?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, AI appointment scheduling allows customers to book appointments anytime, day or night, without requiring staff availability.'
                  }
                },
                {
                  '@type': 'Question',
                  name: 'Does AI scheduling send appointment reminders?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, the system automatically sends appointment reminders via email or SMS to reduce no-shows and keep customers informed.'
                  }
                }
              ]
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30">
            <img
              src="/readdy-assets/appointment-scheduling-hero-001.jpg"
              alt="Appointment Scheduling Hero"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              AI-Powered Appointment Scheduling
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Let clients book appointments 24/7 while you focus on delivering exceptional service. Automated scheduling that eliminates double-bookings and reduces no-shows.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </button>
          </div>
        </section>

        {/* What is Appointment Scheduling Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  What is AI Appointment Scheduling?
                </h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  AI appointment scheduling is an intelligent automation system that allows your clients to book, reschedule, and manage appointments without any manual intervention. Unlike traditional booking methods that require phone calls or email exchanges, our <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI-powered system</Link> provides instant confirmation and seamlessly integrates with your existing calendar.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  For small businesses, time is your most valuable resource. Our appointment scheduling solution eliminates the administrative burden of managing bookings, reduces no-shows through automated reminders, and ensures you never miss a potential client because you were unavailable to answer the phone.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Whether you're a solo practitioner or managing a team, our system scales with your business. From simple one-on-one appointments to complex multi-location, multi-service scheduling, we've got you covered. Combine it with <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">AI phone answering</Link> so callers can book directly over the phone too.
                </p>
              </div>
              <div className="relative h-96 w-full">
                <img
                  src="/readdy-assets/appointment-scheduling-what-001.jpg"
                  alt="AI Appointment Scheduling"
                  className="w-full h-full object-cover rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How Our AI Appointment Scheduling Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A seamless four-step process that transforms how you manage appointments
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: "1",
                  title: "Connect Your Calendar",
                  description: "Integrate your Google Calendar, Outlook, or other calendar platforms in minutes. Set your availability preferences, buffer times, and booking rules."
                },
                {
                  step: "2",
                  title: "Customize Your Booking Page",
                  description: "Brand your booking page with your logo and colors. Configure appointment types, durations, and intake forms to collect the information you need."
                },
                {
                  step: "3",
                  title: "Share Your Link",
                  description: "Embed the booking widget on your website, share your booking link via email or social media, or let our AI Chatbot and Phone Answering service handle bookings automatically."
                },
                {
                  step: "4",
                  title: "Automated Management",
                  description: "The system handles confirmations, sends automated reminders, processes reschedules and cancellations, and keeps your calendar perfectly synchronized."
                }
              ].map((item) => (
                <div key={item.step} className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl font-bold text-primary">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Powerful Features for Small Businesses
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to manage appointments efficiently and professionally
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "ri-calendar-check-line",
                  title: "Two-Way Calendar Sync",
                  description: "Seamlessly integrates with Google Calendar, Outlook, Apple Calendar, and Office 365. Real-time synchronization prevents double-bookings and keeps your schedule accurate across all platforms."
                },
                {
                  icon: "ri-notification-3-line",
                  title: "Automated Reminders",
                  description: "Reduce no-shows by up to 38% with customizable SMS and email reminders. Clients can confirm, reschedule, or cancel directly from reminder messages, giving you advance notice."
                },
                {
                  icon: "ri-palette-line",
                  title: "Custom Booking Pages",
                  description: "Fully branded booking pages with your logo, colors, and custom domain. Add intake forms, display policies, show staff bios, and create a professional booking experience."
                },
                {
                  icon: "ri-shield-check-line",
                  title: "No-Show Protection",
                  description: "Require deposits for appointments, set cancellation policies, collect credit card information, and automatically charge no-show fees. Protect your time and revenue."
                },
                {
                  icon: "ri-team-line",
                  title: "Team Scheduling",
                  description: "Manage appointments for multiple team members, each with their own calendar and availability. Clients can choose specific staff or let the system assign based on availability."
                },
                {
                  icon: "ri-bar-chart-box-line",
                  title: "Analytics & Reporting",
                  description: "Track bookings, no-show rates, popular services, revenue, and client retention. Identify trends, optimize staffing, and make data-driven decisions to grow your business."
                }
              ].map((feature, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-3xl text-primary`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Benefits for Your Small Business
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your scheduling process and unlock new growth opportunities
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "Save 10+ Hours Per Week",
                  description: "Eliminate the back-and-forth of scheduling appointments manually. No more phone tag, missed calls, or email chains. Your clients book instantly, and you get your time back to focus on delivering exceptional service and growing your business."
                },
                {
                  title: "Never Miss a Booking Opportunity",
                  description: "Clients can book appointments 24/7, even when you're sleeping, with family, or serving other customers. Studies show that 67% of clients prefer to book outside of business hours. Don't lose business because you weren't available to answer the phone."
                },
                {
                  title: "Reduce No-Shows by 38%",
                  description: "Automated reminders via SMS and email dramatically reduce no-shows and last-minute cancellations. When clients do need to reschedule, they can do it themselves, giving you advance notice to fill the slot with another client."
                },
                {
                  title: "Deliver a Professional Experience",
                  description: "Instant confirmations, automated reminders, and easy rescheduling create a seamless, professional experience that builds trust and loyalty. Your clients will appreciate the convenience, and you'll stand out from competitors still using manual scheduling."
                },
                {
                  title: "Scale Without Adding Staff",
                  description: "As your business grows, our scheduling system grows with you. Handle hundreds of appointments per week without hiring a receptionist or administrative assistant. Manage multiple locations, team members, and service offerings from one centralized dashboard."
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-lg shadow-md">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Complete Your AI Automation Suite
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Combine appointment scheduling with our other AI services for maximum efficiency
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Link
                to="/services/ai-chatbot"
                className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-chat-4-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                  AI Chatbot
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Let your chatbot handle appointment bookings directly from your website. Clients can ask questions and book appointments in one seamless conversation.
                </p>
                <span className="text-primary font-semibold group-hover:underline">
                  Learn More →
                </span>
              </Link>

              <Link
                to="/ai-phone-answering-service"
                className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-phone-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                  Phone Answering
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our AI phone answering service can book appointments over the phone, check availability in real-time, and confirm bookings instantly.
                </p>
                <span className="text-primary font-semibold group-hover:underline">
                  Learn More →
                </span>
              </Link>

              <Link
                to="/services/ai-receptionist"
                className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <i className="ri-customer-service-2-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                  AI Receptionist
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Combine phone, chat, and scheduling into one AI receptionist that handles all client interactions and appointment management automatically.
                </p>
                <span className="text-primary font-semibold group-hover:underline">
                  Learn More →
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about our appointment scheduling system
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </h3>
                    <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-2xl text-primary flex-shrink-0`}></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-8 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="/readdy-assets/appointment-scheduling-cta-001.jpg"
              alt="CTA Background"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Automate Your Appointment Scheduling?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join hundreds of small businesses saving time and growing revenue with our AI appointment scheduling system. Get started in minutes.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
               <i className="ri-calendar-check-line text-xl"></i>
                Book My Free Call
               <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          title={selectedService}
        />

        <IndustriesModal
          isOpen={isIndustriesModalOpen}
          onClose={() => setIsIndustriesModalOpen(false)}
        />

        <HowItWorksModal
          isOpen={isHowItWorksModalOpen}
          onClose={() => setIsHowItWorksModalOpen(false)}
        />

        <CalendarModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
        />
      </div>
    </>
  );
}